/**
 * Schedule screen — visual timeline of today's medications.
 * Uses localStorage-backed patient data from db.ts.
 */

import type { PatientMedication } from '../../types/index';
import {
  getPatientMedications,
  getTodayDoseLogs,
  addDoseLog,
  type PatientMedRecord,
} from '../../core/db';
import { t, getLangMeta } from '../../i18n';

// -----------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------

interface ScheduleItem {
  medId: string;
  drugName: string;
  strength: string;
  time: string;
  instructions: string;
  status: 'upcoming' | 'taken' | 'missed';
}

type TimePeriod = 'Morning' | 'Afternoon' | 'Evening' | 'Bedtime';

const PILL_IMAGES: Record<string, string> = {
  'metformin': '/pill-images/metformin-500.svg',
  'lisinopril': '/pill-images/lisinopril-10.svg',
  'atorvastatin': '/pill-images/atorvastatin-20.svg',
  'omeprazole': '/pill-images/omeprazole-20.svg',
};

function getPillImageForSchedule(drugName: string): string {
  const lower = drugName.toLowerCase();
  for (const [key, path] of Object.entries(PILL_IMAGES)) {
    if (lower.includes(key)) return path;
  }
  return '/pill-images/default-pill.svg';
}

// -----------------------------------------------------------------------
// Data
// -----------------------------------------------------------------------

function getTimePeriod(time: string): TimePeriod {
  const hour = parseInt(time.split(':')[0], 10);
  if (hour < 12) return 'Morning';
  if (hour < 17) return 'Afternoon';
  if (hour < 21) return 'Evening';
  return 'Bedtime';
}

function formatTime12h(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return hour12 + ':' + String(m).padStart(2, '0') + ' ' + period;
}

function loadScheduleData(): ScheduleItem[] {
  const medRecords = getPatientMedications();
  if (medRecords.length === 0) return [];

  const logRecords = getTodayDoseLogs();

  const takenMap = new Set<string>();
  for (const log of logRecords) {
    if (log.action === 'taken') {
      takenMap.add(log.med_id + '|' + log.scheduled_time);
    }
  }

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const items: ScheduleItem[] = [];

  for (const med of medRecords) {
    for (const timeStr of med.times) {
      const [h, m] = timeStr.split(':').map(Number);
      const medMinutes = h * 60 + m;
      const key = med.id + '|' + timeStr;

      let status: ScheduleItem['status'] = 'upcoming';
      if (takenMap.has(key)) {
        status = 'taken';
      } else if (medMinutes < nowMinutes - 60) {
        status = 'missed';
      }

      items.push({
        medId: med.id,
        drugName: med.drug_name,
        strength: med.strength,
        time: timeStr,
        instructions: med.instructions,
        status,
      });
    }
  }

  items.sort((a, b) => a.time.localeCompare(b.time));
  return items;
}

// -----------------------------------------------------------------------
// Render
// -----------------------------------------------------------------------

export async function renderScheduleScreen(container: HTMLElement): Promise<void> {
  container.textContent = '';
  container.className = 'screen';

  const wrapper = document.createElement('div');
  wrapper.className = 'flex-col gap-md';
  wrapper.style.maxWidth = '600px';
  wrapper.style.margin = '0 auto';

  const header = document.createElement('header');
  header.className = 'screen-header';

  const title = document.createElement('h2');
  title.textContent = t('schedule.title');
  header.appendChild(title);

  const dateEl = document.createElement('span');
  dateEl.className = 'text-muted';
  dateEl.style.fontSize = 'var(--font-size-body)';
  const today = new Date();
  dateEl.textContent = today.toLocaleDateString(getLangMeta().speechLang, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
  header.appendChild(dateEl);
  wrapper.appendChild(header);

  const items = loadScheduleData();

  if (items.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'card text-center';
    empty.style.padding = 'var(--space-2xl) var(--space-lg)';

    const emptyText = document.createElement('p');
    emptyText.className = 'text-muted';
    emptyText.textContent = t('schedule.empty');
    emptyText.style.fontSize = 'var(--font-size-large)';
    empty.appendChild(emptyText);

    wrapper.appendChild(empty);
    container.appendChild(wrapper);
    return;
  }

  // Group by time period
  const groups = new Map<TimePeriod, ScheduleItem[]>();
  const periodOrder: TimePeriod[] = ['Morning', 'Afternoon', 'Evening', 'Bedtime'];

  for (const item of items) {
    const period = getTimePeriod(item.time);
    if (!groups.has(period)) groups.set(period, []);
    groups.get(period)!.push(item);
  }

  const timeline = document.createElement('div');
  timeline.className = 'timeline';
  timeline.setAttribute('role', 'list');
  timeline.setAttribute('aria-label', t('schedule.timelineLabel'));

  const periodIcons: Record<TimePeriod, string> = {
    Morning: '\u2600\uFE0F',
    Afternoon: '\uD83C\uDF24',
    Evening: '\uD83C\uDF05',
    Bedtime: '\uD83C\uDF19',
  };

  for (const period of periodOrder) {
    const periodItems = groups.get(period);
    if (!periodItems) continue;

    const group = document.createElement('div');
    group.className = 'timeline-group';
    group.setAttribute('role', 'listitem');

    const allTaken = periodItems.every((i) => i.status === 'taken');
    const anyMissed = periodItems.some((i) => i.status === 'missed');
    if (allTaken) group.classList.add('taken');
    if (anyMissed) group.classList.add('missed');

    const label = document.createElement('div');
    label.className = 'timeline-label';
    const periodTranslations: Record<TimePeriod, string> = {
      Morning: t('schedule.morning'),
      Afternoon: t('schedule.afternoon'),
      Evening: t('schedule.evening'),
      Bedtime: t('schedule.bedtime'),
    };
    label.textContent = periodIcons[period] + ' ' + periodTranslations[period];
    group.appendChild(label);

    for (const item of periodItems) {
      const card = document.createElement('div');
      card.className = 'card';
      card.style.cssText = 'padding: var(--space-md); margin-bottom: var(--space-sm);';
      card.setAttribute('aria-label',
        item.drugName + ' ' + item.strength + ' at ' + formatTime12h(item.time) + ', ' + item.status);

      const topRow = document.createElement('div');
      topRow.style.cssText = 'display: flex; align-items: center; gap: var(--space-md);';

      // Pill image
      const pillImg = document.createElement('img');
      pillImg.src = getPillImageForSchedule(item.drugName);
      pillImg.alt = item.drugName + ' pill';
      pillImg.style.cssText = 'width: 48px; height: 48px; object-fit: contain; flex-shrink: 0;';
      pillImg.onerror = () => { pillImg.src = '/pill-images/default-pill.svg'; };
      topRow.appendChild(pillImg);

      const drugInfo = document.createElement('div');
      drugInfo.style.cssText = 'flex: 1; min-width: 0;';

      const drugName = document.createElement('div');
      drugName.style.cssText = 'font-size: 17px; font-weight: 700; color: var(--color-text);';
      drugName.textContent = item.drugName.toLowerCase().includes(item.strength.toLowerCase())
        ? item.drugName
        : item.drugName + ' ' + item.strength;
      drugInfo.appendChild(drugName);

      const drugDetail = document.createElement('div');
      drugDetail.className = 'text-muted';
      drugDetail.textContent = formatTime12h(item.time);
      drugInfo.appendChild(drugDetail);

      topRow.appendChild(drugInfo);

      // Status indicator — use visual checkmark for taken
      if (item.status === 'taken') {
        const check = document.createElement('span');
        check.style.cssText = 'font-size: 28px; flex-shrink: 0;';
        check.textContent = '\u2705';
        check.setAttribute('aria-label', t('schedule.taken'));
        topRow.appendChild(check);
      } else {
        const statusBadge = document.createElement('span');
        const statusMap: Record<string, [string, string]> = {
          missed: [t('schedule.missed'), 'badge-danger'],
          upcoming: [t('schedule.upcoming'), 'badge-info'],
        };
        const [statusText, statusClass] = statusMap[item.status] ?? [t('schedule.upcoming'), 'badge-info'];
        statusBadge.className = 'badge ' + statusClass;
        statusBadge.textContent = statusText;
        topRow.appendChild(statusBadge);
      }

      card.appendChild(topRow);

      if (item.instructions) {
        const instrEl = document.createElement('div');
        instrEl.className = 'text-dim mt-sm';
        instrEl.textContent = item.instructions;
        card.appendChild(instrEl);
      }

      if (item.status === 'upcoming') {
        const takeBtn = document.createElement('button');
        takeBtn.className = 'btn btn-success btn-block mt-md';
        takeBtn.textContent = t('schedule.markTaken');
        takeBtn.setAttribute('aria-label', t('schedule.markTakenLabel', { drug: item.drugName }));
        takeBtn.addEventListener('click', () => {
          addDoseLog({
            id: crypto.randomUUID(),
            med_id: item.medId,
            action: 'taken',
            scheduled_time: item.time,
            actual_time: new Date().toISOString(),
          });
          renderScheduleScreen(container);
        });
        card.appendChild(takeBtn);
      }

      group.appendChild(card);
    }

    timeline.appendChild(group);
  }

  wrapper.appendChild(timeline);
  container.appendChild(wrapper);
}
